import { toZonedTime } from "date-fns-tz";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { OrderItem } from "~/components/order/order-item";
import { OrderParcels } from "~/components/order/order-parcels";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { cn } from "~/components/ui/cn";
import { AlertCircleIcon } from "~/components/ui/icons/alert-circle";
import { CheckIcon } from "~/components/ui/icons/check";
import { CopyIcon } from "~/components/ui/icons/copy";
import { ExternalLinkSimpleIcon } from "~/components/ui/icons/external-link-simple";
import { Loader } from "~/components/ui/loader";
import { Rating } from "~/components/ui/rating";
import {
  OrderTagClaim,
  OrderTagReturn,
  OrderTagShipment,
  useOrderQuery,
} from "~/graphql/graphql-new.gen";
import { StoreRegion } from "~/graphql/graphql.gen";
import { orderDetailUpdatedIdState } from "~/hooks/use-order-updated-subscription";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { transformPrice } from "~/utils/currency";
import { formatDate } from "~/utils/date";

import { Copy } from "../copy/copy";
import { OrderInvoiceCustoms } from "./modal-order-invoice-customs";
import { OrderClaimBadge } from "./order-claim-badge";
import { OrderClaimDetails } from "./order-claim-details";
import { OrderDelayAlert } from "./order-delay-alert";
import { OrderFulfillmentBadge } from "./order-fulfillment-badge";
import { OrderMobileHeader } from "./order-mobile-header";
import type { OrderNavigationType } from "./order-navigation";
import { OrderNavigation } from "./order-navigation";
import { OrderPickupPoint } from "./order-pickup-point";
import { OrderPurchaseOrder } from "./order-purchase-order";
import { OrderRecipientAddress } from "./order-recipient-address";
import { OrderRecipientDetails } from "./order-recipient-details";
import { OrderRecipientName } from "./order-recipient-name";
import { OrderRefundBadge } from "./order-refund-badge";
import { OrderRefundDetails } from "./order-refund-details";
import { OrderReshipBadge } from "./order-reship-badge";
import { OrderReshipDetails } from "./order-reship-details";
import { OrderReturn } from "./order-return";
import { OrderReturnBadge } from "./order-return-badge";
import { OrderReturnInitial } from "./order-return-initial";
import { OrderShipmentBadge } from "./order-shipment-badge";
import { OrderShopify } from "./order-shopify";
import { OrderUpdatedBanner } from "./order-updated-banner";
import { ScrollToTop } from "./scroll-to-top";

export type OrderProps = {
  id: string;
} & OrderNavigationType;

export const Order: React.FC<OrderProps> = ({
  goToNextPageAndOrder,
  goToPreviousPageAndOrder,
  hasNextPage,
  hasPreviousPage,
  id,
  nextOrderId,
  previousOrderId,
}: OrderProps) => {
  const { i18n, t } = useTranslation();

  const [orderDetailUpdatedId, setOrderDetailUpdatedId] = useAtom(
    orderDetailUpdatedIdState,
  );
  const activeSale = useAtomValue(activeSaleState);
  const brand = useAtomValue(brandState);

  const {
    data: order,
    isLoading: orderLoading,
    error: orderError,
  } = useOrderQuery(
    {
      orderId: id,
    },
    {
      enabled: !!id,
      select: (result) => result.order,
    },
  );

  useEffect(() => {
    if (orderDetailUpdatedId) {
      setOrderDetailUpdatedId(null);
    }
  }, [order]);

  if (orderLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader className="size-12" />
      </div>
    );
  }

  if ((orderError as Error)?.message === "prisma_order_not_found") {
    return (
      <div className="p-10">
        <Alert appearance="error">
          <AlertCircleIcon />
          <AlertTitle>{t("order.errors.not_found")}</AlertTitle>
          <AlertDescription>
            {t("order.errors.not_found_description")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!order) return null;

  const hasCancelledItems = order.items.some((item) => item.isCancelled);
  const firstReturn = order.returns[0];

  return (
    <div className="relative h-full overflow-auto pt-18 lg:pt-0">
      <ScrollToTop />
      <OrderMobileHeader
        brandId={brand?.id ?? ""}
        goToNextPageAndOrder={goToNextPageAndOrder}
        goToPreviousPageAndOrder={goToPreviousPageAndOrder}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        nextOrderId={nextOrderId}
        previousOrderId={previousOrderId}
      />
      <OrderUpdatedBanner />
      <div className="space-y-10 px-4 pb-12 lg:p-10">
        <div className="pt-6 lg:pt-0">
          <OrderReturnInitial initialOrderId={order.initialOrderId} />
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <OrderFulfillmentBadge
              tag={order.tags.fulfillment}
              isCancelled={order.isCancelled}
            />
            <OrderShipmentBadge
              hasTrackingStucked={order.parcels[0]?.hasTrackingStucked ?? false}
              isCancelled={order.isCancelled}
              tag={order.tags.shipment[0] ?? OrderTagShipment.None}
              trackingSubStatus={order.parcels[0]?.trackingSubStatus}
            />
            <OrderClaimBadge
              tag={order.tags.claim[0] ?? OrderTagClaim.None}
              isCancelled={order.isCancelled}
            />
            <OrderRefundBadge
              tag={order.tags.refund}
              isCancelled={order.isCancelled}
            />
            <OrderReshipBadge
              tag={order.tags.reship}
              isCancelled={order.isCancelled}
            />
            <OrderReturnBadge
              tag={order.tags.return[0] ?? OrderTagReturn.None}
              isCancelled={order.isCancelled}
            />
          </div>
          <div className="mb-4 flex justify-between">
            <div className="space-y-1">
              <div className="flex space-x-1">
                <h4
                  id="modal-order-title"
                  className="text-xl font-bold text-gray-900"
                >
                  {order.id}
                </h4>
                <Copy
                  className="text-gray-500"
                  tabIndex={-1}
                  value={order.id}
                  successMessage={t("clipboard.copied")}
                  trackLabel="order_id"
                >
                  <CopyIcon className="size-4" />
                </Copy>
              </div>
              {order.createdAt ? (
                <p className="text-xs text-[#767A7C]">
                  {formatDate(
                    brand?.timezone
                      ? toZonedTime(new Date(order.createdAt), brand.timezone)
                      : new Date(order.createdAt),
                    i18n.language.replace("_", "-"),
                    {
                      dateStyle: "long",
                      timeStyle: "medium",
                    },
                  )}
                </p>
              ) : null}
              {order.integration.shopify ? (
                <div className="flex items-center gap-1">
                  <a
                    href={order.integration.shopify.orderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-sm font-semibold text-green-900"
                  >
                    {t("order.show_in_shopify")}
                    <ExternalLinkSimpleIcon className="size-4" />
                  </a>
                  {order.integration.shopify.orderCountId ? (
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-700">
                        #{order.integration.shopify.orderCountId}
                      </span>
                      <Copy
                        className="text-gray-500 *:size-3.5"
                        tabIndex={-1}
                        value={order.integration.shopify.orderCountId}
                        successMessage={t("clipboard.copied")}
                        trackLabel="shopify_short_id"
                      >
                        <CopyIcon className="size-4" />
                      </Copy>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <OrderNavigation
              brandId={brand?.id ?? ""}
              className="hidden lg:flex"
              goToNextPageAndOrder={goToNextPageAndOrder}
              goToPreviousPageAndOrder={goToPreviousPageAndOrder}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              nextOrderId={nextOrderId}
              previousOrderId={previousOrderId}
            />
          </div>
          <div className="space-y-4">
            <OrderShopify order={order} />
            <OrderDelayAlert order={order} />
          </div>
        </div>
        <section className="space-y-4">
          <OrderReturn orderReturn={firstReturn} tags={order.tags} />

          <OrderReshipDetails
            reshipOrder={order.reshipOrder}
            reshipOrderId={order.reshipOrderId}
            tags={order.tags}
          />

          <OrderRefundDetails
            items={order.items}
            linesRefunds={order.linesRefunds}
            tags={order.tags}
          />

          <OrderClaimDetails
            claim={order.claims[0]}
            order={order}
            parcel={order.parcels[0]}
            tags={order.tags}
          />

          <OrderParcels
            isFullDigital={order.isFullDigital}
            orderId={order.id}
            parcels={order.parcels}
            shipping={order.shipping}
            tags={order.tags}
          />
        </section>
        <section>
          <header className="flex items-center justify-between py-4">
            <h3 className="text-2xl font-bold text-gray-900">
              {t("order.items")}
            </h3>
            <p className="text-xs">
              <span
                className={cn({
                  "text-gray-300 line-through": hasCancelledItems,
                })}
              >
                {transformPrice(
                  brand?.store === StoreRegion.Fr
                    ? order.totalPriceSold.valueWithVat
                    : order.totalPriceSold.valueWithoutVat || 0,
                  i18n.language === "fr" ? "fr" : "en",
                  order.totalPriceSold.currency.toString().toUpperCase() ||
                    "EUR",
                )}
              </span>{" "}
              {hasCancelledItems ? (
                <span className="ml-2">
                  {transformPrice(
                    order.items
                      .filter((item) => !item.isCancelled)
                      .map((item) =>
                        brand?.store === StoreRegion.Fr
                          ? item.priceSold.valueWithVat
                          : item.priceSold.valueWithoutVat,
                      )
                      .reduce((a, b) => a + b, 0),
                    i18n.language === "fr" ? "fr" : "en",
                    order.totalPriceSold.currency.toString().toUpperCase() ||
                      "EUR",
                  )}
                </span>
              ) : null}
              {order.shipping.fees ? (
                <span>
                  {" "}
                  ({t("with")}{" "}
                  {transformPrice(
                    order.shipping.fees,
                    i18n.language === "fr" ? "fr" : "en",
                    activeSale?.currency.toString().toUpperCase() ?? "EUR",
                  )}{" "}
                  {t("fdp")})
                </span>
              ) : null}
            </p>
          </header>
          <div className="space-y-4">
            {order.items.map((item) => (
              <OrderItem key={item.id} item={item} />
            ))}
          </div>
        </section>
        <section>
          <header className="items-center justify-between py-4 md:flex">
            <h3 className="text-2xl font-bold text-gray-900">
              {t("order.shipping_info")}
            </h3>
            {order.recipient.previous || order.initialOrderId ? (
              <div className="flex items-center">
                <CheckIcon className="mr-1.5 size-4 text-green-600" />
                <div className="text-sm leading-5.5 text-green-600">
                  {order.recipient.previous
                    ? t("order.updated_address")
                    : t("order.new_address")}
                </div>
              </div>
            ) : null}
          </header>
          <div className="space-y-4">
            {order.shipping.pickupPoint ? (
              <>
                <OrderPickupPoint order={order} />
                <OrderRecipientName order={order} />
              </>
            ) : (
              <OrderRecipientAddress order={order} />
            )}

            <OrderRecipientDetails order={order} />
          </div>
        </section>
        {order.rating?.rate ? (
          <section>
            <header className="items-center">
              <h3 className="mb-4 text-2xl font-bold">{t("order.rate")}</h3>
            </header>
            <div className="space-y-4">
              <Rating value={order.rating.rate} />
              <p className="text-sm text-gray-700">{order.rating.message}</p>
            </div>
          </section>
        ) : null}

        <section>
          <header className="items-center">
            <h3 className="mb-4 text-2xl font-bold">{t("order.documents")}</h3>
          </header>
          <div className="space-y-4">
            <OrderPurchaseOrder orderId={order.id} />
            {brand?.store === StoreRegion.Us ? (
              <OrderInvoiceCustoms orderId={order.id} />
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

Order.displayName = "ModalOrder";
